import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import type { OrderDetail, OrderDocHint } from '@/api/orders';
import { Can } from '@/auth/guards';
import { fmtDate } from '../lib';
import { SourceEmailLink } from '../SourceEmailLink';
import { UploadDocumentButton } from '../workLib';
import { SOURCE_KIND_LABEL } from './sourceKinds';

const DOC_HINT_LABEL: Record<string, string> = {
  invoice: 'invoice',
  order_form: 'order form',
  product_list: 'product list',
  duty_invoice: 'duty invoice',
  other: 'file',
};

const LINE_CAPTURE_LABEL: Record<string, string> = {
  captured: 'list captured',
  partial: 'list partially captured',
  external: 'list is external',
};

interface DocRow {
  key: string;
  kind: string;
  name: string;
  date: string | null;
  source: React.ReactNode;
  status: React.ReactNode;
}

function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

/**
 * Every document the journey rests on, in one table: uploaded and fetched
 * files, the emails each document was read from, and the file-shaped refs.
 * Uploading lives here (and on the Missing documents queue) and nowhere else
 * on the journey.
 */
export function DocumentsTab({ detail, onChanged }: { detail: OrderDetail; onChanged: () => void }) {
  const rows = useMemo<DocRow[]>(() => {
    const out: DocRow[] = [];
    const email = (messageId: string, label: string) => (
      <SourceEmailLink messageId={messageId} journeyId={detail.id} label={label} />
    );
    const link = (href: string, label: string) => (
      <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
        {label} <ExternalLink className="h-3 w-3" />
      </a>
    );

    for (const doc of detail.documents) {
      out.push({
        key: `doc-${doc.id}`,
        kind: DOC_HINT_LABEL[doc.docHint ?? 'other'] ?? 'file',
        name: doc.filename,
        date: doc.uploadedAt,
        source: doc.origin === 'fetch'
          ? (doc.sourceUrl ? link(doc.sourceUrl, `fetched from ${hostOf(doc.sourceUrl)}`) : 'fetched')
          : 'uploaded',
        status: doc.processedAt ? `processed ${fmtDate(doc.processedAt)}` : 'awaiting processing',
      });
    }
    detail.announcements.forEach((a, i, arr) => {
      if (!a.sourceMessageId) return;
      out.push({
        key: `ann-${a.id}`,
        kind: 'announcement',
        name: a.sourceFilename || 'announcement email',
        date: a.announcedDate,
        source: email(a.sourceMessageId, arr.length === 1 ? 'email' : `email ${i + 1}`),
        status: a.lineCapture ? LINE_CAPTURE_LABEL[a.lineCapture] : `${a.lines.length} products`,
      });
    });
    const orderSourceIds = Array.from(new Set(detail.orderLines.map((l) => l.sourceMessageId).filter((v): v is string => Boolean(v))));
    orderSourceIds.forEach((id, i) => {
      out.push({
        key: `conf-${id}`,
        kind: 'confirmation',
        name: orderSourceIds.length === 1 ? 'order confirmation' : `order confirmation ${i + 1}`,
        date: detail.placedDate,
        source: email(id, 'email'),
        status: `${detail.orderLines.filter((l) => l.sourceMessageId === id).length} products`,
      });
    });
    for (const inv of detail.invoices) {
      out.push({
        key: `inv-${inv.id}`,
        kind: 'invoice',
        name: inv.invoiceNo || inv.sourceFilename || 'invoice',
        date: inv.invoiceDate,
        source: inv.sourceMessageId ? email(inv.sourceMessageId, 'email') : '—',
        status: `${inv.lines.length} products`,
      });
      for (const s of inv.sources) {
        if (s.messageId && s.messageId === inv.sourceMessageId && !s.filename) continue;
        out.push({
          key: `src-${s.id}`,
          kind: SOURCE_KIND_LABEL[s.docKind] || s.docKind,
          name: s.filename || `${inv.invoiceNo || 'invoice'} source`,
          date: inv.invoiceDate,
          source: s.messageId ? email(s.messageId, 'email') : '—',
          status: `contributed ${s.contributed}`,
        });
      }
    }
    for (const ref of detail.refs) {
      if (ref.refType === 'order_form_file') {
        out.push({ key: `ref-${ref.id}`, kind: 'order form', name: ref.value, date: null, source: '—', status: null });
      } else if (ref.refType === 'product_list_url') {
        out.push({ key: `ref-${ref.id}`, kind: 'product list', name: hostOf(ref.value), date: null, source: link(ref.value, 'open'), status: null });
      } else if (ref.refType === 'payment_link') {
        out.push({ key: `ref-${ref.id}`, kind: 'payment link', name: hostOf(ref.value), date: null, source: link(ref.value, 'open'), status: null });
      }
    }
    return out.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [detail]);

  // An open errand on this journey that expects a document decides what an upload is filed as,
  // so the upload closes it (server: askAutoClose.onDocumentAdded). Two different kinds → 'other'.
  const expectedHint = useMemo<OrderDocHint>(() => {
    const hints = new Set(
      detail.actions
        .filter((a) => a.status === 'open' && a.errand?.upload?.doc_hint)
        .map((a) => a.errand!.upload!.doc_hint)
    );
    return hints.size === 1 ? [...hints][0] : 'other';
  }, [detail.actions]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">{rows.length} document{rows.length === 1 ? '' : 's'}</span>
        <Can code="orders:write:documents">
          <span className="ml-auto"><UploadDocumentButton orderId={detail.id} docHint={expectedHint} label={expectedHint === 'other' ? undefined : `Upload ${DOC_HINT_LABEL[expectedHint]}`} onUploaded={onChanged} /></span>
        </Can>
      </div>
      {rows.length === 0 ? (
        <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground">No documents on this journey yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border/60">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left font-normal">Kind</th>
                <th className="px-2 py-1.5 text-left font-normal">Name</th>
                <th className="px-2 py-1.5 text-left font-normal">Date</th>
                <th className="px-2 py-1.5 text-left font-normal">Source</th>
                <th className="px-2 py-1.5 text-left font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-t border-border/40">
                  <td className="whitespace-nowrap px-2 py-1.5 capitalize text-muted-foreground">{row.kind}</td>
                  <td className="max-w-[24rem] truncate px-2 py-1.5 font-medium">{row.name}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-muted-foreground">{row.date ? fmtDate(row.date) : '—'}</td>
                  <td className="px-2 py-1.5">{row.source}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{row.status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
